import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import {
  Lead,
  LeadSourceHealth,
  DashboardMetrics,
  RoutingStatus,
  ErrorCode,
} from '../types/leads'

// Mock Data Generator
const SOURCES = [
  { name: 'Zillow', list: 'Zillow Hot Leads' },
  { name: 'LendingTree', list: 'LendingTree Queue' },
  { name: 'Realtor.com', list: 'Realtor Priority' },
  { name: 'Website Inquiry', list: 'Organic Inbound' },
  { name: 'Referral', list: 'Partner Referrals' },
  { name: 'Bankrate', list: 'Bankrate Shoppers' },
  { name: 'NerdWallet', list: 'NerdWallet Compare' },
]

const ERROR_TYPES: {
  code: import('../types/leads').ErrorCode
  message: string
}[] = [
  {
    code: 'SF_API_TIMEOUT',
    message:
      'Salesforce API connection timed out after 30000ms. The dialing list queue may be experiencing high load.',
  },
  {
    code: 'INVALID_PHONE',
    message:
      'The provided phone number failed validation rules for the target dialing list. A valid 10-digit US number is required.',
  },
  {
    code: 'DUPLICATE_LEAD',
    message:
      'A lead with this email address already exists in the target dialing list and duplicate merging is disabled.',
  },
  {
    code: 'LIST_NOT_FOUND',
    message:
      'The expected dialing list could not be found in Salesforce. It may have been renamed, archived, or deleted.',
  },
  {
    code: 'AUTH_EXPIRED',
    message:
      'The integration user OAuth token has expired and automatic refresh failed. Manual re-authentication required.',
  },
  {
    code: 'FIELD_MAPPING',
    message:
      'Required custom field mapping failed. The lead source provided data that could not be mapped to the target list schema.',
  },
  {
    code: 'RATE_LIMITED',
    message:
      'Salesforce API rate limit exceeded for the current hour. Lead routing is temporarily paused.',
  },
]

const FIRST_NAMES = [
  'James',
  'Mary',
  'John',
  'Patricia',
  'Robert',
  'Jennifer',
  'Michael',
  'Linda',
  'William',
  'Elizabeth',
  'David',
  'Barbara',
  'Richard',
  'Susan',
  'Joseph',
  'Jessica',
]
const LAST_NAMES = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
]

const generateMockLeads = (): Lead[] => {
  const leads: Lead[] = []
  const now = new Date()
  const startOfDay = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    8,
    0,
    0,
  ) // 8 AM today

  for (let i = 0; i < 45; i++) {
    const sourceObj = SOURCES[Math.floor(Math.random() * SOURCES.length)]
    const firstName =
      FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)]
    const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)]

    // Determine status (~60% routed, ~20% missing, ~10% error, ~10% pending)
    const rand = Math.random()
    let status: RoutingStatus = 'routed'
    let errorCode: import('../types/leads').ErrorCode | undefined
    let errorMessage: string | undefined

    if (rand > 0.6 && rand <= 0.8) status = 'missing'
    else if (rand > 0.8 && rand <= 0.9) {
      status = 'error'
      const errorObj =
        ERROR_TYPES[Math.floor(Math.random() * ERROR_TYPES.length)]
      errorCode = errorObj.code
      errorMessage = errorObj.message
    } else if (rand > 0.9) status = 'pending'

    // Random time today between 8 AM and now
    const createdAt = new Date(
      startOfDay.getTime() +
        Math.random() * (now.getTime() - startOfDay.getTime()),
    ).toISOString()

    // Assign error details for error-status leads
    const errorEntry =
      status === 'error'
        ? ERROR_TYPES[Math.floor(Math.random() * ERROR_TYPES.length)]
        : undefined

    leads.push({
      id: `L-${10000 + i}`,
      name: `${firstName} ${lastName}`,
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
      phone: `(555) ${Math.floor(100 + Math.random() * 900)}-${Math.floor(1000 + Math.random() * 9000)}`,
      leadSource: sourceObj.name,
      expectedDialingList: sourceObj.list,
      status,
      errorCode,
      errorMessage,
      createdAt,
    })
  }

  // Sort by created at descending
  return leads.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
}

export function useLeadData() {
  const [leads, setLeads] = useState<Lead[]>(generateMockLeads())
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [sourceFilter, setSourceFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<RoutingStatus | null>(null)
  const verificationTimers = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Simulate external system verification after 3-8 seconds
  const scheduleVerification = useCallback((leadIds: string[]) => {
    leadIds.forEach((id) => {
      // Clear any existing timer for this lead
      const existing = verificationTimers.current.get(id)
      if (existing) clearTimeout(existing)

      const delay = 3000 + Math.random() * 5000 // 3-8 seconds
      const timer = setTimeout(() => {
        setLeads((prev) =>
          prev.map((lead) =>
            lead.id === id && lead.status === 'pending_verification'
              ? { ...lead, status: 'verified' as RoutingStatus }
              : lead,
          ),
        )
        verificationTimers.current.delete(id)
      }, delay)
      verificationTimers.current.set(id, timer)
    })
  }, [])

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      verificationTimers.current.forEach((timer) => clearTimeout(timer))
    }
  }, [])

  // Derived Data
  const unresolvedLeads = useMemo(() => {
    return leads.filter((lead) => {
      if (
        lead.status === 'resolved' ||
        lead.status === 'routed' ||
        lead.status === 'pending_verification' ||
        lead.status === 'verified'
      )
        return false
      if (sourceFilter && lead.leadSource !== sourceFilter) return false
      if (statusFilter && lead.status !== statusFilter) return false
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        return (
          lead.name.toLowerCase().includes(query) ||
          lead.email.toLowerCase().includes(query) ||
          lead.phone.includes(query)
        )
      }
      return true
    })
  }, [leads, sourceFilter, statusFilter, searchQuery])

  const pendingVerificationLeads = useMemo(() => {
    return leads
      .filter((lead) => lead.status === 'pending_verification')
      .sort(
        (a, b) =>
          new Date(b.resolvedAt || 0).getTime() -
          new Date(a.resolvedAt || 0).getTime(),
      )
  }, [leads])

  const resolvedLeads = useMemo(() => {
    return leads
      .filter(
        (lead) => lead.status === 'verified' || lead.status === 'resolved',
      )
      .sort(
        (a, b) =>
          new Date(b.resolvedAt || 0).getTime() -
          new Date(a.resolvedAt || 0).getTime(),
      )
  }, [leads])

  const metrics = useMemo<DashboardMetrics>(() => {
    const totalLeads = leads.length
    const successfullyRouted = leads.filter(
      (l) =>
        l.status === 'routed' ||
        l.status === 'resolved' ||
        l.status === 'verified',
    ).length
    const missingFromLists = leads.filter(
      (l) => l.status === 'missing' || l.status === 'error',
    ).length
    const successRate =
      totalLeads > 0 ? Math.round((successfullyRouted / totalLeads) * 100) : 0

    return { totalLeads, successfullyRouted, missingFromLists, successRate }
  }, [leads])

  const sourceHealth = useMemo<LeadSourceHealth[]>(() => {
    const healthMap = new Map<string, LeadSourceHealth>()

    SOURCES.forEach((s) => {
      healthMap.set(s.name, {
        name: s.name,
        expectedDialingList: s.list,
        totalToday: 0,
        routedCount: 0,
        missingCount: 0,
        errorCount: 0,
        pendingCount: 0,
        resolvedCount: 0,
      })
    })

    leads.forEach((lead) => {
      const health = healthMap.get(lead.leadSource)
      if (health) {
        health.totalToday++
        if (lead.status === 'routed') health.routedCount++
        else if (lead.status === 'missing') health.missingCount++
        else if (lead.status === 'error') health.errorCount++
        else if (lead.status === 'pending') health.pendingCount++
        else if (lead.status === 'resolved' || lead.status === 'verified')
          health.resolvedCount++
        else if (lead.status === 'pending_verification') health.pendingCount++
      }
    })

    return Array.from(healthMap.values()).sort(
      (a, b) => b.totalToday - a.totalToday,
    )
  }, [leads])

  // Actions
  const toggleSelect = useCallback((id: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    if (
      selectedLeadIds.size === unresolvedLeads.length &&
      unresolvedLeads.length > 0
    ) {
      setSelectedLeadIds(new Set())
    } else {
      setSelectedLeadIds(new Set(unresolvedLeads.map((l) => l.id)))
    }
  }, [unresolvedLeads, selectedLeadIds.size])

  const clearSelection = useCallback(() => {
    setSelectedLeadIds(new Set())
  }, [])

  const pushSelectedToList = useCallback(() => {
    if (selectedLeadIds.size === 0) return

    const now = new Date().toISOString()
    const idsToVerify = Array.from(selectedLeadIds)
    setLeads((prev) =>
      prev.map((lead) => {
        if (selectedLeadIds.has(lead.id)) {
          return {
            ...lead,
            status: 'pending_verification' as RoutingStatus,
            resolvedAt: now,
            errorCode: undefined,
            errorMessage: undefined,
          }
        }
        return lead
      }),
    )
    setSelectedLeadIds(new Set())
    scheduleVerification(idsToVerify)
  }, [selectedLeadIds, scheduleVerification])

  const pushSingleLead = useCallback(
    (id: string) => {
      const now = new Date().toISOString()
      setLeads((prev) =>
        prev.map((lead) =>
          lead.id === id
            ? {
                ...lead,
                status: 'pending_verification' as RoutingStatus,
                resolvedAt: now,
                errorCode: undefined,
                errorMessage: undefined,
              }
            : lead,
        ),
      )
      setSelectedLeadIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      scheduleVerification([id])
    },
    [scheduleVerification],
  )

  const undoResolve = useCallback((id: string) => {
    // Cancel any pending verification timer
    const timer = verificationTimers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      verificationTimers.current.delete(id)
    }
    setLeads((prev) =>
      prev.map((lead) =>
        lead.id === id
          ? {
              ...lead,
              status: 'missing' as RoutingStatus,
              resolvedAt: undefined,
            }
          : lead,
      ),
    )
  }, [])

  const refreshData = useCallback(() => {
    // Clear all verification timers
    verificationTimers.current.forEach((timer) => clearTimeout(timer))
    verificationTimers.current.clear()
    setLeads(generateMockLeads())
    setSelectedLeadIds(new Set())
    setSourceFilter(null)
    setStatusFilter(null)
    setSearchQuery('')
  }, [])

  return {
    leads,
    unresolvedLeads,
    pendingVerificationLeads,
    resolvedLeads,
    metrics,
    sourceHealth,
    selectedLeadIds,
    searchQuery,
    setSearchQuery,
    sourceFilter,
    setSourceFilter,
    statusFilter,
    setStatusFilter,
    toggleSelect,
    selectAll,
    clearSelection,
    pushSelectedToList,
    pushSingleLead,
    undoResolve,
    refreshData,
  }
}
